import {useState} from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {maybe} from '@freckle/maybe'
import type {NonEmptyArray} from '@freckle/non-empty'
import {mkNonEmpty, nonEmptyToArray} from '@freckle/non-empty'
import type {ProductT} from '@freckle/educator-entities/ts/common/helpers/products'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import {Dropdown} from '@freckle/educator-materials/ts/components/dropdown'
import type {TranslateT} from '@freckle/educator-materials/ts/helpers/translate'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {OptionType} from '@freckle/educator-materials/ts/logic/dropdown'
import {groupedOptions} from '@freckle/educator-materials/ts/logic/dropdown'
import type {PracticeTypeFilterT} from '@freckle/school/ts/common/helpers/api-helper'
import {dropdown} from './practice-type-picker.module.scss'

type Props = {
  selected: PracticeTypeFilterT
  onChange: (a: PracticeTypeFilterT) => void
}

const options = Products.all()
const getGroupedProducts = (t: TranslateT) => [
  {
    label: t('PRACTICE_TYPE_PICKER_MENU_MATH_GROUP'),
    options: Products.getMathProducts().map((mathProduct: ProductT) => ({
      label: t('PRODUCT_FILTER_OPTION', {product: mathProduct}),
      value: mathProduct,
    })),
  },
  {
    label: t('PRACTICE_TYPE_PICKER_MENU_ELA_GROUP'),
    options: Products.getElaProducts().map((elaProduct: ProductT) => ({
      label: t('PRODUCT_FILTER_OPTION', {product: elaProduct}),
      value: elaProduct,
    })),
  },
  {
    label: t('PRACTICE_TYPE_PICKER_MENU_SOCIAL_STUDIES_GROUP'),
    options: Products.getSocialStudiesProducts().map((socialStudiesProduct: ProductT) => ({
      label: t('PRODUCT_FILTER_OPTION', {product: socialStudiesProduct}),
      value: socialStudiesProduct,
    })),
  },
  {
    label: t('PRACTICE_TYPE_PICKER_MENU_SCIENCE_GROUP'),
    options: Products.getScienceProducts().map((scienceProduct: ProductT) => ({
      label: t('PRODUCT_FILTER_OPTION', {product: scienceProduct}),
      value: scienceProduct,
    })),
  },
]

export function PracticeTypePicker(props: Props) {
  const {selected, onChange} = props
  const {t} = useSafeTranslation()

  const [selection, setSelection] = useState<Array<ProductT>>(() => {
    switch (selected.tag) {
      case 'all':
        return options
      case 'none':
        return []
      case 'specific':
        return selected.specific
      default:
        return exhaustive(selected)
    }
  })

  const selectAll = () => {
    setSelection(options)
    onChange({tag: 'all'})
  }

  const selectNone = () => {
    setSelection([])
    onChange({tag: 'none'})
  }

  const selectSpecific = (specific: NonEmptyArray<ProductT>) => {
    setSelection(nonEmptyToArray(specific))
    onChange({tag: 'specific', specific})
  }

  const handleSelection = (selectedProducts: ProductT[]) => {
    setSelection(selectedProducts)

    if (options.length === selectedProducts.length) {
      selectAll()
    } else if (selectedProducts.length === 0) {
      selectNone()
    } else {
      maybe(selectAll, selectSpecific, mkNonEmpty(selectedProducts))
    }
  }

  const translateProduct = (product: ProductT) => t('PRODUCT_FILTER_OPTION', {product})

  function formatPracticeTypeOptions(selection: Array<ProductT>): Array<OptionType<ProductT>> {
    return selection.map(product => ({label: translateProduct(product), value: product}))
  }

  function getPracticeTypesSelectionOptions(
    practiceTypesOptions: readonly OptionType<ProductT>[],
  ): Array<ProductT> {
    return practiceTypesOptions.map(({value}) => value)
  }

  return (
    <div className={`hidden-print ${dropdown}`}>
      <div>
        <Dropdown
          options={groupedOptions(getGroupedProducts(t))}
          isSearchable={false}
          isMultiSelect
          handleChange={(practiceTypeOptions: readonly OptionType<ProductT>[]) => {
            const practiceTypes = getPracticeTypesSelectionOptions(practiceTypeOptions)
            handleSelection(practiceTypes)
          }}
          value={formatPracticeTypeOptions(selection)}
          labelText={t('PRACTICE_TYPE_PICKER_LABEL')}
          noneSelectedText={t('NONE_SELECTED_TEXT')}
        />
      </div>
    </div>
  )
}
